import { useEffect } from 'react';
import { MarketingPageShell } from '@/components/marketing/MarketingPageShell';
import { RedesignButton } from '@/components/marketing/redesign/RedesignButton';
import { RdReveal, RdSection } from '@/components/marketing/redesign/RedesignMotion';
import { PRIMARY_SIGNUP_CTA, SCHEDULE_CALL_CTA, VESTA_SALES_CALENDAR } from '@/lib/marketing-cta';
import { usePageMeta } from '@/lib/use-page-meta';

const ANNOTATIONS = [
  {
    style: { top: 80, left: -20 },
    num: 1,
    title: 'KPI strip',
    body: 'RevPAR, ADR, occupancy, GOPPAR with tabular numerals + day-over-day deltas.',
  },
  {
    style: { top: 240, right: -30 },
    num: 2,
    title: 'Daily briefing',
    body: 'Auto-generated each morning. Plain English, cited to your data.',
  },
  {
    style: { bottom: 120, left: -20 },
    num: 3,
    title: 'Anomaly feed',
    body: 'Severity-ranked, tied to property data — not third-party benchmarks.',
  },
  {
    style: { bottom: 60, right: -30 },
    num: 4,
    title: 'Property context',
    body: 'Keys, currency, timezone, PMS — always in view.',
  },
] as const;

const MINI_CARDS = [
  {
    eye: '— 01 Morning',
    title: 'Open Vesta with coffee.',
    body: 'The briefing is waiting. Three bullets, one thing to act on.',
    rows: [
      { label: 'RevPAR', value: '$184.20', tone: 'up' as const },
      { label: 'Occupancy', value: '74.9%', tone: 'dn' as const },
      { label: 'F&B vs plan', value: '+$1.4k', tone: 'up' as const },
    ],
  },
  {
    eye: '— 02 Midday',
    title: 'Ask, don\'t dig.',
    body: '"Why did labor spike Tuesday?" Vesta answers in seconds with cited transactions.',
    rows: [
      { label: 'Anomaly', value: 'Labor +11%', tone: 'warn' as const },
      { label: 'Cause', value: 'Banquet overlap', tone: 'neutral' as const },
      { label: 'Margin', value: '+$1.4k', tone: 'up' as const },
    ],
  },
  {
    eye: '— 03 Friday',
    title: 'Owner report, ready.',
    body: 'Weekly summary printed to PDF or emailed to owners — no manual rollup.',
    rows: [
      { label: 'RevPAR wow', value: '+4.2%', tone: 'up' as const },
      { label: 'GOPPAR wow', value: '+6.4%', tone: 'up' as const },
      { label: 'Runway', value: '7.2 mo', tone: 'neutral' as const },
    ],
  },
] as const;

export default function InsideVesta() {
  usePageMeta({
    title: 'Inside Vesta CFO — Product tour',
    description: 'A working dashboard tour — the same view your GM sees every morning.',
    path: '/inside',
  });

  useEffect(() => {
    document.title = 'Inside Vesta · Vesta CFO';
  }, []);

  return (
    <MarketingPageShell navActive="inside">
      <RdSection tight hero style={{ paddingTop: 80 }}>
        <div className="rd-container">
          <span className="hero-eye">
            <span className="pulse" />
            Inside Vesta CFO
          </span>
          <h1 className="hero-h" style={{ margin: '18px 0 0', fontSize: 'clamp(40px, 5vw, 60px)', maxWidth: 780 }}>
            A working dashboard,
            <br />
            <em>not a screenshot tour.</em>
          </h1>
          <p className="hero-sub" style={{ maxWidth: 580 }}>
            This is the same view your GM sees every morning. Annotated callouts show where operators spend their time.
          </p>

          <div style={{ position: 'relative', marginTop: 48 }}>
            <div className="inside-frame">
              <div className="chrome">
                <span className="dot" style={{ background: '#FF5F57' }} />
                <span className="dot" style={{ background: '#FEBC2E' }} />
                <span className="dot" style={{ background: '#28C840' }} />
                <span className="url">vestacfo.app/dashboard</span>
              </div>
              <img src="/marketing/analytics-dashboard.png" alt="Vesta CFO live dashboard" />
            </div>

            {ANNOTATIONS.map((anno) => (
              <RdReveal key={anno.num} className="inside-anno hidden xl:block" style={anno.style} delay={0.15 + anno.num * 0.08} variant="scaleIn">
                <div className="h">
                  <span className="num">{anno.num}</span>
                  {anno.title}
                </div>
                <div className="b">{anno.body}</div>
              </RdReveal>
            ))}
          </div>

          <div className="inside-mini">
            {MINI_CARDS.map((card) => (
              <div key={card.eye} className="mini-card">
                <div className="eye">{card.eye}</div>
                <h3>{card.title}</h3>
                <p>{card.body}</p>
                <div className="mini-preview">
                  {card.rows.map((row) => (
                    <div
                      key={row.label}
                      className={`row${row.tone === 'up' ? ' up' : row.tone === 'dn' ? ' dn' : ''}`}
                    >
                      <span>{row.label}</span>
                      <b style={row.tone === 'warn' ? { color: '#DC3D3D' } : undefined}>{row.value}</b>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 96 }}>
            <div className="cta-block">
              <div className="h">
                Connect Mews. <em>See your first briefing tomorrow.</em>
              </div>
              <p>Or upload a CSV — Vesta works without an integration. 14-day free trial, no card required.</p>
              <div className="row">
                <RedesignButton variant="gold" size="lg" to="/auth">
                  {PRIMARY_SIGNUP_CTA}
                </RedesignButton>
                <RedesignButton variant="outline-light" size="lg" href={VESTA_SALES_CALENDAR} external>
                  Schedule a 30-min call
                </RedesignButton>
              </div>
            </div>
          </div>
        </div>
      </RdSection>
    </MarketingPageShell>
  );
}
